import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * Swipe-to-dismiss tuning.
 *
 * The drawer only supports drag-to-close on the mobile layout; on desktop it is
 * a persistent 25rem panel and pointer drags would fight with text selection.
 */
const SWIPE_MAX_WIDTH = '(max-width: 749px)';

/** Movement (px) before we commit to treating a gesture as horizontal or vertical. */
const SWIPE_AXIS_LOCK = 8;

/** Fraction of the drawer width that must be dragged away to dismiss on release. */
const SWIPE_CLOSE_RATIO = 0.3;

/** px/ms — a quick flick dismisses even if the drag was short. */
const SWIPE_FLICK_VELOCITY = 0.45;

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /**
   * In-flight swipe gesture, or null when no finger is down.
   * @type {{ startX: number, startY: number, startTime: number, offset: number, axis: 'x' | 'y' | null } | null}
   */
  #swipe = null;

  /** Pending rAF id for the drag, so writes coalesce to one per frame. */
  #dragFrame = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupSwipeToClose();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#teardownSwipeToClose();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Let the drawer be dragged off-screen with a finger, the way a native sheet
   * behaves. Closing is the most common thing a shopper does with an open menu,
   * so it should not require aiming at a small close button.
   */
  #setupSwipeToClose() {
    const { menuDrawer } = this.refs;
    if (!menuDrawer) return;

    // touchmove is non-passive because a horizontal drag must cancel the
    // browser's own scroll/overscroll handling once we've locked to the x axis.
    menuDrawer.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    menuDrawer.addEventListener('touchmove', this.#onTouchMove, { passive: false });
    menuDrawer.addEventListener('touchend', this.#onTouchEnd, { passive: true });
    menuDrawer.addEventListener('touchcancel', this.#onTouchEnd, { passive: true });
  }

  #teardownSwipeToClose() {
    const { menuDrawer } = this.refs;
    if (!menuDrawer) return;

    menuDrawer.removeEventListener('touchstart', this.#onTouchStart);
    menuDrawer.removeEventListener('touchmove', this.#onTouchMove);
    menuDrawer.removeEventListener('touchend', this.#onTouchEnd);
    menuDrawer.removeEventListener('touchcancel', this.#onTouchEnd);
  }

  /** @returns {HTMLElement | null} */
  #getBackdrop() {
    const backdrop = this.querySelector('.menu-drawer__backdrop');
    return backdrop instanceof HTMLElement ? backdrop : null;
  }

  /** @param {TouchEvent} event */
  #onTouchStart = (event) => {
    if (!this.isOpen) return;
    if (event.touches.length !== 1) return;
    if (!window.matchMedia(SWIPE_MAX_WIDTH).matches) return;

    // Don't hijack gestures aimed at a horizontally scrollable strip.
    if (event.target instanceof Element && event.target.closest('.menu-drawer__featured-content-list')) {
      return;
    }

    const touch = event.touches[0];
    if (!touch) return;

    this.#swipe = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: performance.now(),
      offset: 0,
      axis: null,
    };
  };

  /** @param {TouchEvent} event */
  #onTouchMove = (event) => {
    const swipe = this.#swipe;
    const touch = event.touches[0];
    if (!swipe || !touch || event.touches.length !== 1) return;

    const deltaX = touch.clientX - swipe.startX;
    const deltaY = touch.clientY - swipe.startY;

    // Directional lock: decide once, so a vertical scroll of the menu list is
    // never stolen by the drag handler and vice versa.
    if (swipe.axis === null) {
      if (Math.abs(deltaX) < SWIPE_AXIS_LOCK && Math.abs(deltaY) < SWIPE_AXIS_LOCK) return;

      swipe.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';

      if (swipe.axis === 'x') {
        this.refs.menuDrawer.classList.add('menu-drawer--dragging');
      }
    }

    if (swipe.axis !== 'x') return;

    // The drawer is anchored left, so only leftward travel closes it. Dragging
    // right past the edge is damped rather than tracked 1:1, which is what iOS
    // does at the end of a scroll and stops the panel detaching from the edge.
    swipe.offset = deltaX <= 0 ? deltaX : Math.pow(deltaX, 0.55);
    event.preventDefault();

    this.#scheduleDragFrame();
  };

  /**
   * Commit the drag to the DOM at most once per frame.
   *
   * touchmove can fire more often than the compositor paints, so writing the
   * transform inline on every event does redundant style work and makes the
   * drag feel like it is lagging the finger. Coalescing to rAF means one write
   * per frame, always with the newest position.
   */
  #scheduleDragFrame() {
    if (this.#dragFrame != null) return;

    this.#dragFrame = requestAnimationFrame(() => {
      this.#dragFrame = null;

      const swipe = this.#swipe;
      if (!swipe || swipe.axis !== 'x') return;

      const { menuDrawer } = this.refs;
      const width = menuDrawer.offsetWidth || 1;
      const progress = Math.min(1, Math.abs(Math.min(0, swipe.offset)) / width);

      menuDrawer.style.transform = `translateX(${swipe.offset}px)`;

      // Fade the scrim in step with the drag so the gesture feels connected.
      const backdrop = this.#getBackdrop();
      if (backdrop) backdrop.style.opacity = `${1 - progress}`;
    });
  }

  #cancelDragFrame() {
    if (this.#dragFrame == null) return;
    cancelAnimationFrame(this.#dragFrame);
    this.#dragFrame = null;
  }

  #onTouchEnd = () => {
    const swipe = this.#swipe;
    this.#swipe = null;
    this.#cancelDragFrame();

    if (!swipe || swipe.axis !== 'x') return;

    const { menuDrawer } = this.refs;
    const width = menuDrawer.offsetWidth || 1;
    // Only leftward travel counts toward dismissal; the damped rightward
    // overscroll must never trigger a close.
    const distance = Math.abs(Math.min(0, swipe.offset));
    const velocity = distance / Math.max(1, performance.now() - swipe.startTime);
    const shouldClose = distance > width * SWIPE_CLOSE_RATIO || velocity > SWIPE_FLICK_VELOCITY;

    // Hand control back to CSS *before* closing. `close()` inspects
    // `getAnimations()` synchronously to know when the drawer has finished
    // moving, so the transition has to be un-suppressed by the time it runs —
    // otherwise it sees no animation and tears the drawer down instantly.
    menuDrawer.classList.remove('menu-drawer--dragging');
    menuDrawer.style.transform = '';

    const backdrop = this.#getBackdrop();
    if (backdrop) backdrop.style.opacity = '';

    // The stylesheet's closed position (-100%) becomes the transition target,
    // so the panel carries on from wherever the finger left it, no snap-back.
    if (shouldClose) this.close();
  };

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
